<script setup lang="ts">
import { computed, inject, onMounted } from 'vue'

import { Figure, Image, ImageSrcSet, Picture, Badge } from '@toni/brand-ui'
import { BauerProductCard, PurchaseOptions } from '@toni/commerce-api-types'

import PurchaseOption from '../PurchaseOption.vue'
import AlternativePurchaseOptions from '../AlternativePurchaseOptions.vue'
import { BUTTON_TYPE } from '../../utils/types'
import { ProductCardTrackingType } from '../../utils/types.ts'
import { useIntersectionObserver } from '../../utils/productCardTracking'

const props = defineProps<{ productCard: BauerProductCard, purchaseOptions: PurchaseOptions }>()
const purchaseOptions = computed(() => props.purchaseOptions || [])
const primaryPurchaseOption = purchaseOptions.value[0]
const alternativePurchaseOptions = purchaseOptions.value.slice(1)

// Get the product card tracking data
const productCardTracking = inject('productCardTracking') satisfies ProductCardTrackingType
const bauerProductCardIdComputed = computed(() => props.productCard.bauerProductCardId)

// Set up the intersection observer for scroll tracking
const {
  productCardScrollTrackingRef,
  setupIntersectionObserver
} = useIntersectionObserver({
  placementType: productCardTracking?.product_card_type,
  productCardPosition: productCardTracking?.product_card_position,
  productCardRelativePosition: productCardTracking?.product_card_rel_position,
  bauerProductCardId: bauerProductCardIdComputed.value
})

onMounted(() => {
  if (setupIntersectionObserver) {
    setupIntersectionObserver()
  }
})
</script>

<template>
  <div ref="productCardScrollTrackingRef"
       class="flex flex-col gap-2">
    <div class="flex flex-col gap-2 items-center flex-1 justify-start">
      <Image>
        <Figure class="flex flex-1">
          <Picture class="flex-1 flex items-center justify-center w-[7.5rem] h-[7.5rem] sm:w-[10rem] sm:h-[10rem]">
            <ImageSrcSet>
              <img :src="productCard?.images?.[0]?.url"
                   :alt="productCard?.images?.[0]?.altText"
                   class="max-w-[7.5rem] max-h-[7.5rem] sm:max-w-[10rem] sm:max-h-[10rem] mb-4">
            </ImageSrcSet>
          </Picture>
        </Figure>
      </Image>
      <div class="min-h-[1.875rem]">
        <Badge v-if="productCard.label"
               type="text"
               variant="ghost"
               class="text-badge-lg px-2 pt-1 pb-[3px]">
          {{ productCard.label }}
        </Badge>
      </div>
      <h3 class="min-h-[2.75rem] font-heading text-center text-heading-xs text-foreground line-clamp-2">
        {{ productCard?.title }}
      </h3>
      <PurchaseOption v-if="primaryPurchaseOption"
                      :bauer-product-card-id="bauerProductCardIdComputed"
                      :purchase-option="primaryPurchaseOption"
                      :purchase-option-position="1"
                      size="md"
                      variant="highlighted"
                      :type="BUTTON_TYPE.icon" />
    </div>
    <AlternativePurchaseOptions
      :alternative-purchase-options="alternativePurchaseOptions"
      :bauer-product-card-id="bauerProductCardIdComputed"
      :type="BUTTON_TYPE.icon" />
  </div>
</template>
